import { type ResolvedDemoContent, resolveDemoContent } from './demoContent';
import type {
  DemoClinicIdentity,
  DemoEnvironmentSettingsOutput,
  DemoEnvironmentStatus,
  DemoTeamMember,
} from '../../services/api/types.gen';

/**
 * How each status reads in the console.
 *
 * The badge collapses two independent facts — freshness, and whether the last
 * run failed — because the operator only acts on the most urgent one. The
 * precedence itself is computed on the server, so this file only decides
 * wording. Whether the nightly refresh covers a sandbox is not part of the
 * badge; it is the sandbox's own setting, shown as its own toggle.
 */
export interface DemoEnvironmentStatusPresentation {
  label: string;
  className: string;
  hint: string;
}

/**
 * When the nightly refresh re-applies RogerDoc coding data and feature flags.
 * Must match the demo-state-refresh-daily schedule in the backend.
 */
export const DEMO_REFRESH_TIME_LABEL = '6:00 AM PT';

/**
 * The refresh toggle's tooltip. Only the demo stage has the schedule, so
 * elsewhere the toggle is disabled and says so rather than promising a refresh.
 *
 * The nightly job skips a sandbox that has never been set up, since there is
 * nothing to put back; the "on" hint says so rather than claiming a refresh.
 */
export function refreshToggleHint({
  scheduled,
  on,
  setUp,
  clickable = true,
}: {
  scheduled: boolean;
  on: boolean;
  setUp: boolean;
  /**
   * Whether the reader can act on this. The rail draws the same state as an
   * icon nobody can click, and asked for the sentence by trimming the call to
   * action off this one -- which made a reword here silently tell people to
   * click something inert.
   */
  clickable?: boolean;
}): string {
  if (!scheduled)
    return 'The nightly refresh runs on the demo environment only.';
  if (!on) {
    const state = 'Not refreshed nightly.';
    return clickable
      ? `${state} Click to refresh it every night at ${DEMO_REFRESH_TIME_LABEL}.`
      : state;
  }
  const state = setUp
    ? `Refreshed nightly at ${DEMO_REFRESH_TIME_LABEL}.`
    : `Refreshed nightly at ${DEMO_REFRESH_TIME_LABEL} once it has been run.`;
  return clickable ? `${state} Click to turn off.` : state;
}

export const DEMO_ENVIRONMENT_STATUS_PRESENTATION: Record<
  DemoEnvironmentStatus,
  DemoEnvironmentStatusPresentation
> = {
  hidden: {
    label: 'Hidden',
    className: 'border-border bg-muted text-muted-foreground',
    hint: 'Hidden from My sandboxes. Unhide to bring it back — the settings are kept.',
  },
  running: {
    label: 'Running',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
    hint: 'A run is in progress. Expand the row to follow its steps.',
  },
  failed: {
    label: 'Failed',
    className: 'border-red-200 bg-red-50 text-red-800',
    hint: 'The last run did not finish. Expand the row to see which step failed.',
  },
  never_run: {
    label: 'Never run',
    className: 'border-border bg-muted text-muted-foreground',
    hint: 'Not run from this console yet.',
  },
  stale: {
    label: 'Stale',
    className: 'border-amber-200 bg-amber-50 text-amber-800',
    hint: 'Nobody has touched this in weeks. Run it again to bring it up to date.',
  },
  idle: {
    label: 'Idle',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
    hint: 'Set up recently, but nobody has signed in yet.',
  },
  active: {
    label: 'Active',
    className: 'border-green-200 bg-green-50 text-green-800',
    hint: 'Somebody signed in recently.',
  },
};

// What needs attention first. Hidden sorts last: it is retired on purpose, so it
// is the one row nobody has to do anything about.
const STATUS_ORDER: DemoEnvironmentStatus[] = [
  'running',
  'failed',
  'stale',
  'never_run',
  'idle',
  'active',
  'hidden',
];

export function compareByUrgency(
  a: DemoEnvironmentStatus,
  b: DemoEnvironmentStatus
): number {
  return STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b);
}

/**
 * Settings with every field present.
 *
 * The generated type makes each field optional because the backend model gives
 * them all defaults, so reading one straight off the response is
 * `T | undefined` everywhere. Resolving once here keeps that from spreading
 * `?? default` through the dialogs, where a missed one would silently save a
 * different setting than the operator saw.
 */
export type ResolvedDemoEnvironmentSettings = {
  include_coding: boolean;
  demo_ff_template_agency: string;
  demo_team: DemoTeamMember[];
  /**
   * The agency's own people to invite.
   *
   * Kept apart from ``demo_team`` because the two are chosen differently: the
   * Roger team is the same handful every time and has a saved default, and
   * these are different for every sandbox and must never reach that default.
   */
  agency_team: DemoTeamMember[];
  refresh_nightly: boolean;
  content: ResolvedDemoContent;
  /**
   * Kinnser clinic ids a build does not create.
   *
   * A setup choice rather than a fact about the agency, and different from the
   * clinics the Kinnser sync leaves out: that one decides what the 6am sync
   * writes to a real agency, and a demo staging choice must not change it.
   */
  excluded_clinic_ids: string[];
  /** Typed NPI, EIN, CCN / PTAN and address, by Kinnser clinic id. */
  clinic_identity: Record<string, DemoClinicIdentity>;
};

export function resolveSettings(
  settings: DemoEnvironmentSettingsOutput | undefined,
  defaultTemplateAgency: string
): ResolvedDemoEnvironmentSettings {
  return {
    include_coding: settings?.include_coding ?? true,
    demo_ff_template_agency:
      settings?.demo_ff_template_agency ?? defaultTemplateAgency,
    demo_team: settings?.demo_team ?? [],
    agency_team: settings?.agency_team ?? [],
    refresh_nightly: settings?.refresh_nightly ?? true,
    content: resolveDemoContent(settings?.content),
    excluded_clinic_ids: [...(settings?.excluded_clinic_ids ?? [])],
    clinic_identity: { ...(settings?.clinic_identity ?? {}) },
  };
}

/**
 * The system name, only when it adds something.
 *
 * For most agencies the written name and the system name are the same string,
 * so printing both shows the name twice. Returns null in that case — same rule
 * the retired setup form used for its agency labels.
 */
export function distinctSystemName(
  writtenName: string | null | undefined,
  systemName: string
): string | null {
  const written = (writtenName ?? '').trim();
  const system = systemName.trim();
  if (!written || written.toLowerCase() === system.toLowerCase()) {
    return null;
  }
  return system;
}
